import { LogManager, ModelRow, StringUtil } from "@mcleod/core";
import { AutogenLayoutInvoiceProcessing } from "./autogen/AutogenLayoutInvoiceProcessing";
import { ClickEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceRemoteDataChangeEvent, Label, Layout, ModelDataChangeType, SlideoutDecorator, Snackbar, Tab, TableRowCreationEvent, Toast } from "@mcleod/components";
import { ModelInvoiceProcessingProfile, RowInvoiceProcessingProfile } from "./models/ModelInvoiceProcessingProfile";
import { LMEUserSettings } from "@mcleod/general";
import { InvoiceProcessingFilter } from "./InvoiceProcessingFilter";
import { InvoiceProcessingProcessing } from "./InvoiceProcessingProcessing";
import { InvoiceProcessingTableType } from "./InvoiceProcessingTableType";
import { InvoiceReadyToProcessList } from "./InvoiceReadyToProcessList";
import { InvoiceProcessingOnHold } from "./InvoiceProcessingOnHold";
import { InvoiceProcessingTableListeners } from "./InvoiceProcessingUtil";
import { InvoiceProcessingCreditRebillSlideout } from "./InvoiceProcessingCreditRebillSlideout";
import { InvoiceProcessingCreditRebillErrorSlideout } from "./InvoiceProcessingCreditRebillErrorSlideout";
import { ManageStops } from "@mcleod/dispatch/src/ManageStops";
import { RearrangeStopSlideout } from "@mcleod/dispatch";

const log = LogManager.getLogger("lme.ar.InvoiceProcessing");
interface InvoiceProcessingTab {
    type: InvoiceProcessingTableType;
    layout?: InvoiceProcessingProcessing | InvoiceProcessingOnHold | InvoiceReadyToProcessList;
    tab?: Tab;
}

export function openManageStops(event: ClickEvent) {
    const label = event.target as Label & { boundRow?: ModelRow };
    const orderID = label.boundRow?.get("order_id");
    if (orderID) {
        // Used in onClickListener for Manage Stops button
        const slideout = new RearrangeStopSlideout("O", orderID);
        slideout.showSlideout(
            (manageLayout: ManageStops) => { },
            (canceled: boolean) => {
                if (!canceled) {
                    Toast.showSuccessToast("Changes were saved successfully.");
                }

            }
        );
    }
}
export class InvoiceProcessing extends AutogenLayoutInvoiceProcessing {
    private static readonly DEBOUNCE_DELAY_MS = 100;

    private tabs: InvoiceProcessingTab[] = [
        { type: InvoiceProcessingTableType.PENDING_REVIEW },
        { type: InvoiceProcessingTableType.ON_HOLD },
        { type: InvoiceProcessingTableType.READY_TO_PROCESS }
    ];

    private _profileRow: RowInvoiceProcessingProfile;
    private filterObject: InvoiceProcessingFilter;
    private _invoiceProcessingProfile: string;
    private showingErrorToast: boolean;
    private updateDebounceTimer: NodeJS.Timeout | null = null;

    /**
     * Clears the debounce timer and resets it to null
     */
    private clearDebounceTimer() {
        if (this.updateDebounceTimer) {
            clearTimeout(this.updateDebounceTimer);
            this.updateDebounceTimer = null;
        }
    }

    override async onLoad() {
        this.setupTables();

        // Register cleanup handler for component unmount
        this.addUnmountListener(() => {
            this.clearDebounceTimer();
        });
    }


    // called from MultiProfilePlanningTabs.ts
    async loadProfile(profile?: string | ModelRow) {
        log.debug("Loading profile")
        if (profile instanceof ModelRow) {
            this.setProfileRow(profile);
            return;
        }
        const profileId = profile ?? this.invoiceProcessingProfile ?? LMEUserSettings.get()?.invoice_processing_profile;
        if (profileId == null)
            Snackbar.showSnackbar("You do not have a default invoice processing profile defined. "
                + "Add a default profile to your preferences so your invoices will automatically "
                + "display or you can select a profile above to get started.", { persist: true });
        else {
            const profileRow = await new ModelInvoiceProcessingProfile().searchSingle({ id: profileId });
            if (profileRow == null)
                Snackbar.showSnackbar("The profile [" + profileId + "] was not found.", { persist: true });
            else
                this.setProfileRow(profileRow);
        }
    }

    public setProfileRow(value: ModelRow) {
        if (value != this._profileRow) {
            // Cancel any pending updates from previous profile
            this.clearDebounceTimer();

            this._profileRow = value;
            this.filterObject = new InvoiceProcessingFilter(value);
            if (this._profileRow != null) {
                this.searchMainDatasource();
            }
        }
        const layoutReady = this.findComponentById("layoutInvoiceReadyToProcess") as InvoiceReadyToProcessList;
        if (layoutReady) {
            layoutReady.profileRow = this.profileRow;
        }
    }

    override get windowTitle(): string {
        const title = "Invoice Processing";
        const profileId = this.profileRow?.getString("id");
        return profileId ? `${title} - ${profileId}` : title;
    }

    private setSecondaryFilters() {
    }

    public get profileRow(): RowInvoiceProcessingProfile {
        return this._profileRow;
    }

    searchMainDatasource() {
        log.debug("searchMainDatasource called");
        this.mainDataSource.clear();
        this.mainDataSource.search({
            profile_row: this.profileRow
        }).then(() => {
            log.debug("Main datasource search completed, total records:", this.mainDataSource.data?.length || 0);
            // Log some details about the records for debugging (only if debug is enabled to avoid performance impact)
            if (log.isDebugEnabled() && this.mainDataSource.data && this.mainDataSource.data.length > 0) {
                this.mainDataSource.data.forEach((row, index) => {
                    log.debug(`Record ${index + 1}:`, {
                        order_id: row.get("order_id"),
                        is_ready_to_process: row.get("is_ready_to_process"),
                        hold_reason_code: row.get("hold_reason_code"),
                        total_charges: row.get("total_charges")?.amount
                    });
                });
            }
            this.setSecondaryFilters();
        });
    }

    sourceInvoiceProcessingAfterExecution(event) {
        log.debug("sourceInvoiceProcessingAfterExecution called", {
            action: event.getAction(),
            actionIsSearch: DataSourceAction.SEARCH == event.getAction(),
            dataSourceRecords: this.mainDataSource.data?.length || 0
        });

        if (DataSourceAction.SEARCH == event.getAction()) {
            log.debug("Triggering automatic updateTabTitles and populateFilteredTables");
            this.updateTabTitles();
            this.populateFilteredTables();
        }
    }

    sourceInvoiceProcessingBeforeExecution(event: DataSourceExecutionEvent) {
        if (DataSourceAction.SEARCH == event.getAction()) {
            this.tabs.forEach(tab => {
                if (tab.layout != null) {
                    tab.layout.tableProcessing.busy = true
                }
            });
        }
    }

    /** This is an event handler for the onSubscriberError event of sourceBrokerageMovements.  */
    sourceInvoiceProcessingCacheOnSubscriberError(event) {
        log.error(event.error);
        if (!this.showingErrorToast) {
            this.showingErrorToast = true;
            Toast.showToast("There was a problem keeping this page up-to-date real time.  Try refreshing the page.", null, { persist: true, onDismiss: () => this.showingErrorToast = false });
        }
    }

    sourceInvoiceProcessingCacheOnRemoteDataChange(event: DataSourceRemoteDataChangeEvent) {
        let eventChangeType = event.change.type;
        const changedRow = event.change.data as ModelRow;
        const billingId = changedRow.get("billing_id");

        // Check if the record already exists in the main data source (was loaded from server with proper filtering)
        const existsInMainDataSource = this.mainDataSource.data.some(row => row.get("billing_id") === billingId);

        // Determine if we have valid responsibility data to evaluate.
        // responsibility_codes is populated when responsibility is explicitly set on the movement.
        const hasResponsibilityData = !StringUtil.isEmptyString(changedRow.get("responsibility_codes"));

        // For responsibility changes to work in real-time, we need to re-evaluate the profile filter.
        // However, we must be careful not to break tab-to-tab movement (e.g., put on hold):
        // - If record has responsibility_codes: always re-evaluate (responsibility may have changed)
        // - If record exists but has no responsibility_codes: trust server-side filtering, allow tab movement
        // - For new records: always evaluate the profile filter
        // DELETE events skip filter evaluation since they're removing the record regardless.
        const profileFilterPassed = eventChangeType === ModelDataChangeType.DELETE ||
            (existsInMainDataSource && !hasResponsibilityData) ||
            this.filterObject.dataPasses(event.change.data);

        if (!profileFilterPassed) {
            eventChangeType = ModelDataChangeType.DELETE;
        }

        // Update individual tabs with the specific record change (preserves selections)
        // IMPORTANT: Always evaluate the tab filter regardless of eventChangeType.
        // When responsibility filter converts UPDATE to DELETE, we still need to check each tab's filter
        // to determine if the record should be ADDED to a different tab (e.g., moving Ready to Process -> Hold).
        // However, we should ONLY add records if the profile filter passed - otherwise we'd be adding
        // records that don't belong to this profile.
        for (let i = 0; i < this.tabs.length; i++) {
            // Always evaluate the tab filter to determine what action to take
            const passesFilter = this.tabs[i].layout.type.filter(changedRow);

            const existingRowIndex = this.tabs[i].layout.mainDataSource.data.findIndex(row => row.get("billing_id") === billingId);
            const existsInTab = existingRowIndex >= 0;

            const isDeleteEvent = event.change.type === ModelDataChangeType.DELETE;

            // When profile filter fails (e.g., responsibility changed to exclude user), we must DELETE
            // the record from all tabs regardless of whether the tab filter passes. The tab filter
            // only checks hold/ready status - not responsibility. So a record could pass the tab filter
            // but still need to be removed because the user no longer has access via responsibility.
            const shouldDelete = !profileFilterPassed || isDeleteEvent;


            // Determine the action based on filter result and current state:
            // - shouldDelete=true, exists=true  -> DELETE (profile filter failed or DELETE event)
            // - shouldDelete=true, exists=false -> NO-OP
            // - passesFilter=true, exists=true, profilePassed=true  -> UPDATE the record
            // - passesFilter=true, exists=false, profilePassed=true -> ADD the record
            // - passesFilter=false, exists=true  -> DELETE the record (moved to another tab)
            // - passesFilter=false, exists=false -> NO-OP (record not relevant to this tab)
            if (shouldDelete) {
                // Profile filter failed (responsibility changed) or explicit DELETE event
                if (existsInTab) {
                    const changeForTable = { type: ModelDataChangeType.DELETE, data: changedRow };
                    this.tabs[i].layout.mainDataSource.handleDataChange(changeForTable);
                }
                // else: NO-OP - record wasn't in this tab anyway
            } else if (passesFilter) {
                if (existsInTab) {
                    // UPDATE existing row
                    const changeForTable = { type: ModelDataChangeType.UPDATE, data: changedRow };
                    this.tabs[i].layout.mainDataSource.handleDataChange(changeForTable);
                } else if (profileFilterPassed) {
                    // ADD new row (for tab-to-tab movement or new records)
                    this.tabs[i].layout.mainDataSource.addRow(changedRow);
                }
                // else: NO-OP - record passes tab filter but doesn't belong to this profile
            } else {
                if (existsInTab) {
                    // DELETE: remove from this tab (moved to another tab)
                    const changeForTable = { type: ModelDataChangeType.DELETE, data: changedRow };
                    this.tabs[i].layout.mainDataSource.handleDataChange(changeForTable);
                }
                // else: NO-OP - record doesn't pass filter and wasn't in tab
            }

            this.tabs[i].layout.tableProcessing.rows.forEach(tableRow => InvoiceProcessingTableListeners.onRowDisplayOrderCount(tableRow, tableRow.data, this.tabs[i].layout.tableProcessing));
        }

        // Update the main data source directly without triggering search execution
        // This prevents sourceInvoiceProcessingAfterExecution from running and calling populateFilteredTables
        if (eventChangeType == ModelDataChangeType.UPDATE) {
            const existingRowIndex = this.mainDataSource.data.findIndex(row => row.get("billing_id") === billingId);
            if (existingRowIndex >= 0) {
                // Update existing row directly
                this.mainDataSource.data[existingRowIndex] = changedRow;
            } else {
                // Add new row directly
                this.mainDataSource.data.push(changedRow);
            }
        } else if (eventChangeType == ModelDataChangeType.DELETE) {
            const existingRowIndex = this.mainDataSource.data.findIndex(row => row.get("billing_id") === billingId);
            if (existingRowIndex >= 0) {
                this.mainDataSource.data.splice(existingRowIndex, 1);
            }
        }

        // Debounce only tab title updates and filter count updates - NOT full table repopulation
        this.clearDebounceTimer();

        // Update tab titles immediately to prevent flashing
        this.updateTabTitlesImmediate();

        this.updateDebounceTimer = setTimeout(() => {
            // Update filter counts after individual record changes
            this.tabs.forEach(tab => {
                const layout = tab.layout as InvoiceProcessingProcessing | InvoiceProcessingOnHold | InvoiceReadyToProcessList;
                if (layout?.filterPanel) {
                    layout.filterPanel.updateFilterCounts();
                }
            });
        }, InvoiceProcessing.DEBOUNCE_DELAY_MS);

        event.preventDefault();
    }

    updateTabTitlesImmediate() {
        // For immediate updates during record moves, use the individual tab counts
        this.tabs.forEach((tab, index) => {
            if (tab.layout == null) return;
            tab.layout.tableProcessing.busy = false;
            // Use the actual count from the individual tab's data source for immediate update
            const actualCount = tab.layout.mainDataSource.data?.length ?? 0;
            tab.tab.caption = tab.type.caption + " " + actualCount;
        });
    }

    updateTabTitles() {
        const tabMap = this.getTabAndFilterCounts();
        this.tabs.forEach((tab, index) => {
            if (tab.layout == null) return;
            tab.layout.tableProcessing.busy = false;
            const tabCountMap = tabMap[tab.type.tableIdSuffix];
            tab.tab.caption = tab.type.caption + " " + (tabCountMap?.totalCount ?? 0);
        });
    }

    populateFilteredTables(tableType?: InvoiceProcessingTableType) {
        log.debug("populateFilteredTables called", {
            specificTableType: tableType?.caption || "all tables",
            totalTabs: this.tabs.length,
            mainDataSourceRecords: this.mainDataSource.data?.length || 0
        });

        this.tabs.forEach((tab, index) => {
            if ((tableType != null && tableType != tab.type) || tab.layout == null)
                return;

            this.populateFilteredTable(tab, index);
        })
    }

    populateFilteredTable(tab: InvoiceProcessingTab, index: number) {
        const layout = tab.layout;
        const modelRows = this.mainDataSource.data.filter(row => layout.filter(row));

        // Preserve selection state across table repopulation
        const selectedBillingIds = new Set<string>();
        if (layout.tableProcessing?.selectedRows?.length > 0) {
            layout.tableProcessing.selectedRows.forEach(row => {
                const billingId = row.data.get("billing_id");
                if (billingId) {
                    selectedBillingIds.add(billingId);
                }
            });
        }

        layout.mainDataSource.setRowsAndMode(DataSourceMode.NONE, modelRows, null);

        // Restore selection for rows that still exist after repopulation
        if (selectedBillingIds.size > 0) {
            // Use setTimeout to ensure the table rows are fully rendered before attempting to restore selection
            setTimeout(() => {
                layout.tableProcessing.rows.forEach(tableRow => {
                    const billingId = tableRow.data.get("billing_id");
                    if (billingId && selectedBillingIds.has(billingId)) {
                        tableRow.selected = true;
                    }
                });
            }, 0);
        }

        if (index != this.tabsetMain.selectedIndex && layout.tableProcessing.rowCount > 0) {
            // the following will populate up to 5 rows when the table is hidden.
            // This avoids populating them when the user switches tab
            const rowsToPopulate = Math.min(5, layout.tableProcessing.rowCount)
            for (let i = 0; i < rowsToPopulate; i++)
                layout.tableProcessing.rows[i]?.populateDOMIfNeeded();
        }
    }

    setupTables() {
        for (let i = 0; i < this.tabs.length; i++) {
            const tab = this.tabsetMain.components.find((component: Component) => component.caption === `${this.tabs[i].type.caption}`) as Tab;
            const layout = tab?.components[0];
            if (layout instanceof InvoiceProcessingProcessing || layout instanceof InvoiceProcessingOnHold || layout instanceof InvoiceReadyToProcessList) {
                layout.configureTableForType(this.tabs[i].type);
                this.tabs[i].layout = layout;
                this.tabs[i].tab = tab;
                layout.tableProcessing.addRowCreateListener((event: TableRowCreationEvent) => event.getTable().busy = false);
            }
        }
    }

    getTabAndFilterCounts() {
        const countMap = {};
        const tabIndexMap = {};
        this.tabs.forEach((tab, index) => {
            countMap[tab.type.tableIdSuffix] = { totalCount: 0 };
            tabIndexMap[tab.type.tableIdSuffix] = index;
        })

        this.mainDataSource.data.forEach(row => {
            const types: InvoiceProcessingTableType[] = [];
            if ((!row.get("is_ready_to_process") && StringUtil.isEmptyString(row.get("hold_reason_code"))) ||
                 (row.get("total_charges")?.amount == 0) && StringUtil.isEmptyString(row.get("hold_reason_code"))) {
                types.push(this.tabs[tabIndexMap["Pending Review"]].type);
            } else if (!StringUtil.isEmptyString(row.get("hold_reason_code"))) {
                types.push(this.tabs[tabIndexMap["Hold"]].type);
            } else if (row.get("is_ready_to_process") && row.get("total_charges")?.amount > 0 &&
                      (row.get("hold_reason_code") === undefined || StringUtil.isEmptyString(row.get("hold_reason_code")))) {
                types.push(this.tabs[tabIndexMap["Ready to Process"]].type);
            }

            types.forEach((type: InvoiceProcessingTableType) => {
                countMap[type.tableIdSuffix]["totalCount"] += 1;

                if (type.secondaryFilters) {
                    type.secondaryFilters.forEach(sFilter => {
                        if (!countMap[type.tableIdSuffix][sFilter.caption])
                            countMap[type.tableIdSuffix][sFilter.caption] = { id: sFilter.id, caption: sFilter.caption, totalCount: 0 };
                        if (sFilter.filter(row)) {
                            countMap[type.tableIdSuffix][sFilter.caption]["totalCount"] += 1;
                        }
                    })
                }
            })
        })

        return countMap;
    }

    get invoiceProcessingProfile() {
        return this._invoiceProcessingProfile;
    }

    set invoiceProcessingProfile(input: string) {
        this._invoiceProcessingProfile = input;
    }

    creditRebillOnClick() {
        const layout = Layout.getLayout("lme/ar/InvoiceProcessingCreditRebillSlideout", {
            width: window.innerWidth * .75,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as InvoiceProcessingCreditRebillSlideout;

        new SlideoutDecorator({
            layout,
            title: "Credit/Rebill",
            fillVerticalSpace: true,
            addlComponents: [layout.buttonNextStep],
            doAfterSlideIn: () => {
                layout.textboxRebillBy.selectedItem = layout.textboxRebillBy.items[0];
            },
            doAfterSlideOut: () => {
                if (layout.errorMessage.length > 0) {
                    this.openErrorSlideout(layout.errorMessage);
                }
                if (layout.successMessage) {
                    Toast.showSuccessToast(layout.successMessage);
                }
            }
        })
    }

    openErrorSlideout(errorMessage: any) {
        const layout = Layout.getLayout("lme/ar/InvoiceProcessingCreditRebillErrorSlideout", {
            width: window.innerWidth * .40,
            backgroundColor: "defaultBackground",
            fillHeight: true,
            paddingBottom: 50
        }) as InvoiceProcessingCreditRebillErrorSlideout;

        new SlideoutDecorator({
            layout,
            title: "Errors From Credit/Rebill Process",
            fillVerticalSpace: true,
            doAfterSlideIn: () => {
                layout.tableErrorMessages.data = errorMessage;
                layout.tableErrorMessages.displayData(null, errorMessage, 0);
            }
        })
    }
}
